import { useCallback, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, validateProjectName, validateProjectNameLooksLikeEmailSubject } from 'entities';

import { Modal, Button, Row, LabeledInput } from 'components/Blocks';
import { CirclePlayIcon, XIcon } from 'components/Icons';

import { ProjectNameAdvisory } from 'pages/Projects/components';

import { useRoutePush } from 'routes';
import { useProjectProfile } from '../../context';
import { useConfirmation } from 'providers/Confirmation';

interface StartModalParams {
  close: () => void;
  originProject: string;
  open: boolean;
}

export function StartWaveModal({ close, open, originProject }: StartModalParams) {
  const [name, setName] = useState('');
  const { project } = useProjectProfile();

  const navigateToProject = useRoutePush('projectProfile');

  const { t } = useLang();
  const { requestConfirmation } = useConfirmation();

  const fillingError = validateProjectName({
    project: {
      ...project,
      name: name.trim(),
    },

    clients: [{ id: project?.client, name: project?.clientName }],
  });

  const newName = name.trim();

  const newWave = actions.projects.startNewTrackerWave.use({
    block: !newName || newName.length <= 4,

    params: {
      origin: originProject,
      name: newName,
    },

    onSuccess: ({ id }) => navigateToProject({ projectId: id }),
  });

  const start = useCallback(async () => {
    const requireConfirmation = validateProjectNameLooksLikeEmailSubject(newName);

    if (!requireConfirmation) return newWave.onClick();

    requestConfirmation({
      onConfirm: newWave.onClick,

      title: t('confirm-project-name')!,

      size: 'medium',

      text: <ProjectNameAdvisory name={newName} />,
    });
  }, [newName, newWave, t, requestConfirmation]);

  return (
    <Modal
      isOpen={open}
      close={close}
      title={t('start-new-wave') as string}
      width="max(40vw, 330px)"
    >
      <p>
        {t(
          'this-will-create-a-new-project-with-the-same-owners-as-the-previous-wave-if-any-user-is-currently-unavailable-it-will-go-through-the-assignment-flow',
        )}
      </p>

      <LabeledInput
        id="name"
        value={name}
        onChange={({ target }) => setName(target.value)}
        label={t('new-project-name')}
        error={!!fillingError && t(fillingError)}
      />

      <Row justifyContent="space-between">
        <Button variant="outlined" onClick={close}>
          <XIcon />
          {t('cancel')}
        </Button>

        <Button disabled={name.length < 4} loading={newWave.loading} onClick={start}>
          <CirclePlayIcon />
          {t('start-wave')}
        </Button>
      </Row>
    </Modal>
  );
}
